/**
 * ESTABLISHMENT-IMPORT-SETTINGS-MODAL
 * Assistant d'import, avant la relecture : familles du document, puis chaque barème et les colonnes
 * qui ne correspondent pas au compte. Chaque famille du document recoit un nom, modifiable : celui de
 * la famille du compte qui lui correspond, sinon le sien. A la fin, un nom deja dans le compte rejoint
 * sa famille, un autre cree une famille, un nom vide laisse le risque sans famille.
 * Barème du document, meme identique a celui du compte : un compte a un seul etablissement le reprend, dans
 * l'editeur des parametres, et le modifie au besoin (il devient son barème) ; un
 * compte multi-etablissement garde le sien (defini pour tout le compte) et y fait correspondre chaque
 * niveau du document.
 * Un compte a un seul etablissement confirme ensuite ses niveaux de risque residuel, a ajuster aux barèmes repris.
 */
import Button from "@/components/button"
import ListDropdown from "@/components/list-dropdown"
import ListFields from "@/components/list-fields"
import ListItem from "@/components/list-item"
import Modal from "@/components/modal"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import riskFamilyStore from "@/entities/risk-family/model/risk-family.store"
import { createEmptyRiskFamily, RiskFamily } from "@/entities/risk-family/model/risk-family.types"
import { createCodeFromName } from "@/entities/risk-family/model/risk-family.utils"
import { PhysicalRiskScaleItem } from "@/entities/physical-risk/model/physical-risk.types"
import RiskScaleSection from "@/entities/physical-risk/ui/RiskScaleSection"
import type { EditableScaleKey } from "@/entities/physical-risk/ui/RiskScalesConfig"
import {
  addResidualScaleItem,
  addScaleItem,
  editScaleItem,
  getResidualScaleItems,
  getResidualSettings,
  removeScaleItem,
  ResidualScaleItem,
  SCALE_VALUE_MIN,
  sortResidualScaleItems,
  sortScaleItems
} from "@/entities/physical-risk/utils/sortScales"
import { accountRisksSettingsEdit, accountUpdate } from "@/redux/account.actions"
import { createRiskAttribute, normalizeRiskAttributes } from "@/redux/account.types"
import { STATUS_LOADED, STATUS_SAVING } from "@/redux/_status.types"
import { faArrowsUpDown } from "@fortawesome/free-solid-svg-icons"
import getColor from "@/utils/get-color.utils"
import { useState } from "react"
import { toast } from "react-toastify"
import {
  applyMapping,
  withFamilyColumn,
  DuerpMapping,
  getAccountScale,
  getDocumentFamilies,
  getDocumentAttributes,
  getDocumentScale,
  getGaps,
  getInitialMapping,
  ImportedDocument,
  matchKey,
  RawDocument,
  ScaleKey
} from "../../model/duerp-mapping.utils"
import ListInput from "@/components/list-input"
import RiskAttributesEditor from "@/entities/physical-risk/ui/RiskAttributesEditor"
import { RawDocumentDownloadButton } from "./EstablishmentImportReviewModal"

//Ordre des etapes de barème ; une etape par barème lu dans le document
const SCALE_STEPS: { scale: ScaleKey; label: string }[] = [
  { scale: "frequency", label: "fréquence" },
  { scale: "severity", label: "gravité" },
  { scale: "prevention", label: "prévention" }
]
//Choix des attributs, en plus de ceux du compte
const NONE_ID = "none"

type Step = "families" | ScaleKey | "residual" | "attributes"
//Niveau du barème repris, avec la valeur du document qu'il remplace (absente pour un niveau ajoute)
type DraftScaleItem = PhysicalRiskScaleItem & { docValue?: number }

interface Props {
  raw: RawDocument
  onDone: (document: ImportedDocument) => void
  onClose: () => void
}

export default function EstablishmentImportSettingsModal({ raw, onDone, onClose }: Props) {
  const dispatch = useAppDispatch()
  const settings = useAppSelector((state) => state._session.accountRisksSettings)
  const riskFamilies: RiskFamily[] = useAppSelector((state) => state.riskFamily.list)
  const attributes = normalizeRiskAttributes(settings.riskAttributes)
  const [gaps] = useState(() => getGaps(raw, riskFamilies, attributes))
  const [mapping, setMapping] = useState<DuerpMapping>(() => getInitialMapping(raw, settings, riskFamilies, attributes))
  const [documentColumns] = useState(() => {
    const usedIds = new Set<string>()
    return getDocumentAttributes(raw).map((source) => {
      const existing = attributes.find(
        (attribute) => attribute.id === mapping.attributes[source] && !usedIds.has(attribute.id)
      )
      const attribute = { ...(existing ?? createRiskAttribute(source)), name: source }
      usedIds.add(attribute.id)
      return { source, attribute }
    })
  })
  const [draftAttributes, setDraftAttributes] = useState(() => documentColumns.map((column) => column.attribute))
  const [familyColumnId, setFamilyColumnId] = useState<string | null>(null)
  const familyColumn = documentColumns.find((column) => column.attribute.id === familyColumnId)?.source
  const document = withFamilyColumn(raw, familyColumn)
  const documentFamilies = getDocumentFamilies(document)
  //Nom de la famille importee : celui de la famille du compte rapprochee, sinon celui du document
  const [names, setNames] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      documentFamilies.map((name) => [
        name,
        riskFamilies.find((family) => family.id === mapping.families[name])?.name ?? name
      ])
    )
  )
  //Barèmes repris : conserver les coefficients du document, y compris les décimales.
  const [draftScales, setDraftScales] = useState<Partial<Record<ScaleKey, DraftScaleItem[]>>>(() =>
    Object.fromEntries(
      gaps.scales.map((scale) => {
        const items = getDocumentScale(raw, scale)
        //Niveau sans libelle dans le document : celui du compte de meme valeur
        const accountScale = getAccountScale(settings, scale)
        return [
          scale,
          items.map((item) => {
            const accountLevel = accountScale.find((level) => level.value === item.value)
            return {
              label: item.label || accountLevel?.label || String(item.value),
              description: item.description || accountLevel?.description || "",
              value: item.value,
              docValue: item.value
            }
          })
        ]
      })
    )
  )
  const [draftResidual, setDraftResidual] = useState(() => getResidualScaleItems(settings))
  const [status, setStatus] = useState(STATUS_LOADED)

  const steps: { id: Step; label: string }[] = [
    { id: "families", label: "Familles de risques" },
    ...SCALE_STEPS.filter((step) => gaps.scales.includes(step.scale)).map((step) => ({
      id: step.scale,
      label: `Barème de ${step.label}`
    })),
    ...(!settings.allowMultipleEstablishments ? [{ id: "residual" as Step, label: "Risque résiduel" }] : []),
    ...(!settings.allowMultipleEstablishments || gaps.attributes.length
      ? [{ id: "attributes" as Step, label: "Colonnes" }]
      : [])
  ]
  const [step, setStep] = useState<Step>("families")
  const stepIndex = steps.findIndex((item) => item.id === step)

  function edit<K extends keyof DuerpMapping>(key: K, changes: Partial<DuerpMapping[K]>) {
    setMapping({ ...mapping, [key]: { ...mapping[key], ...changes } })
  }

  async function handleNext() {
    if (stepIndex < steps.length - 1) return setStep(steps[stepIndex + 1].id)

    setStatus(STATUS_SAVING)
    try {
      //Nom deja dans le compte (ou deja cree pour une autre ligne) : sa famille ; sinon une nouvelle famille
      const families: Record<string, string | null> = {}
      const created: RiskFamily[] = []
      const existingCodes = new Set(riskFamilies.map((family) => family.code))
      for (const name of documentFamilies) {
        const familyName = (names[name] ?? name).trim()
        if (!familyName) {
          families[name] = null
          continue
        }
        const existing = [...riskFamilies, ...created].find((family) => matchKey(family.name) === matchKey(familyName))
        if (existing) {
          families[name] = existing.id
          continue
        }
        const code = createCodeFromName(familyName, existingCodes)
        existingCodes.add(code)
        const family = await dispatch(
          riskFamilyStore.thunks.createEntity(createEmptyRiskFamily({ name: familyName, code }))
        ).unwrap()
        created.push(family)
        families[name] = family.id
      }

      //Barèmes repris : ils remplacent ceux du compte ; chaque valeur du document
      //prend celle de son niveau, ou du niveau le plus proche s'il a ete supprime
      const nextSettings = { ...settings }
      const scales = { ...mapping.scales }
      const importedAttributes = settings.allowMultipleEstablishments
        ? attributes
        : draftAttributes
            .filter((attribute) => attribute.id !== familyColumnId && attribute.name.trim())
            .map((attribute) => ({ ...attribute, name: attribute.name.trim() }))
      const attributeMapping = settings.allowMultipleEstablishments
        ? mapping.attributes
        : Object.fromEntries(
            documentColumns.map(({ source, attribute }) => [
              source,
              importedAttributes.some((item) => item.id === attribute.id) ? attribute.id : null
            ])
          )
      if (!settings.allowMultipleEstablishments) {
        for (const scale of gaps.scales) {
          const items = sortScaleItems(draftScales[scale] ?? [])
          const fromDocument = items.filter((item) => item.docValue !== undefined)
          const levels = items.map(({ docValue, ...level }) => level)
          const values = Object.fromEntries(
            getDocumentScale(raw, scale).map((item) => {
              const level = fromDocument.reduce<DraftScaleItem | undefined>(
                (best, current) =>
                  !best || Math.abs(current.docValue! - item.value) < Math.abs(best.docValue! - item.value)
                    ? current
                    : best,
                undefined
              )
              return [String(item.value), (level ?? items[0])?.value ?? SCALE_VALUE_MIN]
            })
          )
          nextSettings[`${scale}Scale`] = levels
          scales[scale] = values
          dispatch(accountRisksSettingsEdit({ key: `${scale}Scale`, value: levels }))
        }
        const residual = getResidualSettings(sortResidualScaleItems(draftResidual))
        Object.assign(nextSettings, residual)
        dispatch(accountRisksSettingsEdit({ key: "residualRiskScale", value: residual.residualRiskScale }))
        dispatch(accountRisksSettingsEdit({ key: "residualRiskLevels", value: residual.residualRiskLevels }))
        nextSettings.riskAttributes = importedAttributes
        dispatch(accountRisksSettingsEdit({ key: "riskAttributes", value: importedAttributes }))
        await dispatch((_, getState) => dispatch(accountUpdate(getState().account.active)))
      }

      onDone(
        applyMapping(
          document,
          {
            ...mapping,
            families,
            attributes: Object.fromEntries(
              Object.entries(attributeMapping).map(([label, id]) => [label, id === NONE_ID ? null : id])
            ),
            scales
          },
          nextSettings,
          importedAttributes
        )
      )
    } catch (error) {
      console.error("[duerp] Impossible d'enregistrer les paramètres d'import", error)
      toast.error(
        "Les paramètres d’import n’ont pas pu être enregistrés. Votre document est conservé, vous pouvez réessayer."
      )
    } finally {
      setStatus(STATUS_LOADED)
    }
  }

  function renderFamilies() {
    return (
      <>
        <ListFields className="gap-[24px]">
          <div className="flex-1 min-w-[200px]">Famille du document</div>
          <div className="flex-1 min-w-[200px]">Famille importée</div>
        </ListFields>
        {documentFamilies.map((name) => (
          <ListItem key={name} className="items-center gap-[24px]">
            <div className="flex-1 min-w-[200px]">
              <b>{name}</b>
            </div>
            <div className="flex-1 min-w-[200px]">
              <ListInput
                value={names[name] ?? name}
                emptyValue={<i className="grey-t">Non défini</i>}
                onEditEnd={(value) => setNames({ ...names, [name]: value })}
              />
            </div>
          </ListItem>
        ))}
      </>
    )
  }

  //Edition d'un barème repris, comme dans les parametres : niveaux tries par valeur
  function updateDraftScale(scaleKey: EditableScaleKey, updater: (items: DraftScaleItem[]) => DraftScaleItem[]) {
    const scale = scaleKey.replace("Scale", "") as ScaleKey
    setDraftScales({
      ...draftScales,
      [scale]: sortScaleItems(updater(draftScales[scale] ?? []))
    })
  }

  //Maitrise du document notee a l'envers (0,25 = bien maitrise, le risque est multiplie) : la prevention
  //divise le risque, chaque niveau prend l'inverse de sa valeur (G x F x 0,25 = G x F / 4)
  function invertPrevention() {
    if (!settings.allowMultipleEstablishments) {
      return setDraftScales({
        ...draftScales,
        prevention: sortScaleItems(
          (draftScales.prevention ?? []).map((item) => ({ ...item, value: Number((1 / item.value).toFixed(2)) }))
        )
      })
    }
    //Compte multi-etablissement : les niveaux du document correspondent au compte dans l'ordre inverse
    const levels = getDocumentScale(raw, "prevention")
    const current = mapping.scales.prevention
    const values = Object.fromEntries(
      levels.map((level, index) => [String(level.value), current[String(levels[levels.length - 1 - index].value)]])
    )
    edit("scales", { prevention: values })
  }

  function renderPreventionDirection() {
    return (
      <div className="flex items-center gap-[12px] mb-[12px]">
        <div
          className="w-[120px] h-[6px] rounded-full"
          style={{ background: `linear-gradient(to right, ${getColor("red")}, ${getColor("green")})` }}
        />
        <span className="grey-t">Du haut vers le bas : de la moins bonne à la meilleure prévention</span>
        <Button icon={faArrowsUpDown} onClick={invertPrevention}>
          Inverser
        </Button>
      </div>
    )
  }

  function renderScale(scale: ScaleKey, title: string) {
    if (!settings.allowMultipleEstablishments) {
      return (
        <>
          {scale === "prevention" && renderPreventionDirection()}
          <RiskScaleSection
            title={title}
            scaleKey={`${scale}Scale`}
            items={draftScales[scale] ?? []}
            onEditItem={(scaleKey, index, patch) =>
              updateDraftScale(scaleKey, (items) => editScaleItem(items, index, patch))
            }
            onAddItem={(scaleKey, index) => updateDraftScale(scaleKey, (items) => addScaleItem(items, index))}
            onRemoveItem={(scaleKey, index) => updateDraftScale(scaleKey, (items) => removeScaleItem(items, index))}
          />
        </>
      )
    }

    //Compte multi-etablissement : son barème ne change pas, chaque niveau du document y correspond
    const levels = getAccountScale(settings, scale).map((level) => ({
      id: String(level.value),
      name: `${level.value} - ${level.label}`
    }))
    return (
      <>
        {scale === "prevention" && renderPreventionDirection()}
        <ListFields className="gap-[24px]">
          <div className="flex-1 min-w-[200px]">Niveau du document</div>
          <div className="flex-1 min-w-[200px]">Niveau du compte</div>
        </ListFields>
        {getDocumentScale(raw, scale).map((item) => {
          const active = String(mapping.scales[scale][String(item.value)])
          return (
            <ListItem key={item.value} className="items-center gap-[24px]">
              <div className="flex-1 min-w-[200px]">
                <div>{`${item.value}${item.label ? ` - ${item.label}` : ""}`}</div>
                {item.description && <div className="grey-t">{item.description}</div>}
              </div>
              <div className="flex-1 min-w-[200px]">
                <ListDropdown
                  isCaretAlwaysVisible
                  active={active}
                  value={levels.find((level) => level.id === active)?.name}
                  values={levels}
                  onSelect={(level) => {
                    const values = { ...mapping.scales[scale], [item.value]: Number(level.id) }
                    edit("scales", { [scale]: values })
                  }}
                />
              </div>
            </ListItem>
          )
        })}
      </>
    )
  }

  //Niveaux de risque residuel, comme dans les parametres
  function renderResidual() {
    const update = (updater: (items: ResidualScaleItem[]) => ResidualScaleItem[]) =>
      setDraftResidual(updater(draftResidual))
    return (
      <RiskScaleSection
        title="Risque résiduel"
        scaleKey="residualRiskScale"
        items={draftResidual}
        isFirstValueFixed
        isDescriptionHidden
        withColor
        onEditItem={(_, index, patch) =>
          update((items) => editScaleItem(items, index, patch as Partial<ResidualScaleItem>))
        }
        onAddItem={(_, index) => update((items) => addResidualScaleItem(items, index))}
        onRemoveItem={(_, index) => update((items) => removeScaleItem(items, index))}
      />
    )
  }

  function renderAttributes() {
    if (!settings.allowMultipleEstablishments)
      return (
        <>
          <div className="flex gap-[24px] pl-[24px] pr-[72px] grey-t mb-[12px]">
            <div className="flex-1 min-w-[200px]">Colonne du document</div>
            <div className="flex-1 min-w-[200px]">Colonne importée</div>
          </div>
          <RiskAttributesEditor
            items={draftAttributes}
            onChange={setDraftAttributes}
            onRemove={(attribute) => {
              setDraftAttributes(draftAttributes.filter((item) => item.id !== attribute.id))
              if (familyColumnId === attribute.id) setFamilyColumnId(null)
            }}
            familyColumnId={familyColumnId}
            onSelectFamilyColumn={setFamilyColumnId}
            renderSource={(attribute) => documentColumns.find((column) => column.attribute.id === attribute.id)?.source}
          />
        </>
      )
    const values = [
      ...attributes.map((attribute) => ({ id: attribute.id, name: attribute.name })),
      { id: NONE_ID, name: "Ignorer" }
    ]
    return (
      <>
        <ListFields className="gap-[24px]">
          <div className="flex-1 min-w-[200px]">Colonne du document</div>
          <div className="flex-1 min-w-[200px]">Attribut du compte</div>
        </ListFields>
        {gaps.attributes.map((label) => (
          <ListItem key={label} className="items-center gap-[24px]">
            <div className="flex-1 min-w-[200px]">{label}</div>
            <div className="flex-1 min-w-[200px]">
              <ListDropdown
                isCaretAlwaysVisible
                active={mapping.attributes[label] ?? NONE_ID}
                value={values.find((value) => value.id === (mapping.attributes[label] ?? NONE_ID))?.name}
                values={values}
                onSelect={(value) => edit("attributes", { [label]: value.id })}
              />
            </div>
          </ListItem>
        ))}
      </>
    )
  }

  const scaleStep = SCALE_STEPS.find((item) => item.scale === step)
  const subtitle =
    step === "families"
      ? "Confirmez l'import des familles de risques"
      : step === "attributes"
        ? "Confirmez les colonnes de vos risques"
        : step === "residual"
          ? "Confirmez les niveaux de risque résiduel"
          : `Confirmez le barème de ${scaleStep?.label}`

  return (
    <Modal
      title="Importer mon document unique"
      disableClickOutside
      onClose={onClose}
      wizardSteps={steps}
      activeWizardStepId={step}
      onWizardStepClick={(id) => setStep(id as Step)}
      wizardOwlText={subtitle}
      onNext={handleNext}
      onNextCta={stepIndex < steps.length - 1 ? "Suivant" : "Continuer"}
      status={status}
      footerStart={<RawDocumentDownloadButton raw={raw} />}>
      <div>
        {step === "families" && renderFamilies()}
        {scaleStep && renderScale(scaleStep.scale, `Barème de ${scaleStep.label}`)}
        {step === "residual" && renderResidual()}
        {step === "attributes" && renderAttributes()}
      </div>
    </Modal>
  )
}
